import { roundMoney } from "@shared/lib/money";
import { CrossBorderCountriesSchema, type CrossBorderCountries, type SkuRegionPrice } from "@shared/schemas/enums/crossBorder.enum";
import type { TaxConfiguration } from "@shared/types";

export { roundMoney };

/**
 * Cross-border KS (Kaffeesteuer / coffee tax) + VAT pricing.
 *
 * The roaster enters a single bruto price per field for their HOME region, with
 * two toggles describing what that value already includes (VAT / KS). From the
 * recovered net price we rebuild the home-region breakdown and derive every
 * other ship-to country's read-only prices.
 *
 * Rates (VAT% and KS-per-kg) are sourced from the backend Tax Rate API
 * via `useTaxRates`; `DEFAULT_TAX_RATES_BY_REGION` is the fallback
 * used until that endpoint is wired in. All money is rounded to 2 dp.
 */

/** Per-region tax rates. `vatRate` is a fraction (0.07 === 7%); `ksPerKg` is the
 *  coffee tax per kilogram in the SKU currency (0 when not applicable). */
export interface RegionTaxRates {
  vatRate: number;
  ksPerKg: number;
}

export type TaxRatesByRegion = Record<CrossBorderCountries, RegionTaxRates>;

/** Fallback rates until the Tax Rate API lands. DE: KS €2.19/kg + 7%
 *  VAT; NL: no KS + 9% VAT. */
export const DEFAULT_TAX_RATES_BY_REGION: TaxRatesByRegion = {
  DE: { vatRate: 0.07, ksPerKg: 2.19 },
  NL: { vatRate: 0.09, ksPerKg: 0 },
};

/** Grams per bag-size unit — mirrors lambdas `updateProductWeight` so the
 *  front-end KS estimate matches the persisted product weight. */
const GRAMS_PER_UNIT: Record<string, number> = {
  oz: 28.3495,
  lb: 453.592,
  kg: 1000,
  gm: 1,
  gr: 1,
  g: 1,
};

const KG_PER_LB = 0.453592;
const GRAMS_PER_KG = 1000;

/** Whole-percent multiplier — converts a VAT fraction (0.07) to a percent (7). */
const PERCENT = 100;

/** Converts a VAT rate fraction (0.07) to a whole-number percentage (7), rounded.
 *  Used for the "VAT {{vat}}%" rates badge. */
export const vatRateToPercent = (vatRate: number): number => Math.round(vatRate * PERCENT);

/** Strips tax from a tax-inclusive (gross) price: `net = gross / (1 + rate)`. */
export const removeTax = (gross: number, rate: number): number => gross / (1 + rate);

/** Applies tax to a net price: `gross = net × (1 + rate)`. */
export const addTax = (net: number, rate: number): number => net * (1 + rate);

/**
 * Converts a bag-size label (e.g. "10.5 oz", "250 G", "1 kg") to kilograms.
 * Returns null when the label is empty or its unit is unrecognised.
 */
export const parseBagSizeToKg = (bagSize: string | null | undefined): number | null => {
  if (!bagSize) return null;
  const match = /^([\d.]+)\s*([a-zA-Z]+)$/.exec(bagSize.trim());
  if (!match) return null;
  const [, sizeRaw, unitRaw] = match;
  const size = Number.parseFloat(sizeRaw ?? "");
  const gramsPerUnit = GRAMS_PER_UNIT[(unitRaw ?? "").toLowerCase()];
  if (!Number.isFinite(size) || gramsPerUnit === undefined) return null;
  return (size * gramsPerUnit) / GRAMS_PER_KG;
};

/** Coffee tax (Kaffeesteuer) for a region's bag weight, rounded to 2 dp. 0 when
 *  the weight is unknown or the region has no KS. */
export const ksForRegion = (weightKg: number | null, rates: RegionTaxRates): number =>
  weightKg !== null && Number.isFinite(weightKg) ? roundMoney(weightKg * rates.ksPerKg) : 0;

/**
 * Parses a Salesforce-style ";"-separated ship-to string ("NL;DE") into the
 * recognised cross-border countries, trimmed, upper-cased and de-duplicated.
 */
export const parseShipToCountries = (shipTo: string | null | undefined): CrossBorderCountries[] => {
  if (!shipTo) return [];
  const seen = new Set<CrossBorderCountries>();
  for (const segment of shipTo.split(";")) {
    const parsed = CrossBorderCountriesSchema.safeParse(segment.trim().toUpperCase());
    if (parsed.success) seen.add(parsed.data);
  }
  return [...seen];
};

/**
 * Parses a single ISO country code (e.g. "NL", "de") into a recognised
 * cross-border country, trimmed and upper-cased. Returns undefined when the
 * value is empty or not a cross-border destination.
 */
const parseCrossBorderCountry = (value: string | null | undefined): CrossBorderCountries | undefined => {
  if (!value) return undefined;
  const parsed = CrossBorderCountriesSchema.safeParse(value.trim().toUpperCase());
  return parsed.success ? parsed.data : undefined;
};

/**
 * Maps a vendor region code (e.g. "BDE") to its cross-border home
 * country (DE / NL) by reading the trailing two characters. Returns undefined
 * for non-cross-border regions.
 */
export const vendorRegionToCrossBorderCountry = (region: string | undefined): CrossBorderCountries | undefined =>
  region ? parseCrossBorderCountry(region.trim().slice(-2)) : undefined;

/**
 * Resolves the roaster's editable home region for cross-border pricing. The
 * vendor's Salesforce billing `countryCode` is authoritative: an NL roaster can
 * be provisioned under the "BDE" region (the BDE Salesforce hierarchy covers
 * all of Benelux), so the region slice alone ("BDE" → "DE")
 * would be wrong for them. The country code therefore wins; the region code is
 * the fallback when no recognised country code is stored. Returns undefined for
 * non-cross-border vendors so variants keep the single-price grid.
 */
export const homeRegionForVendor = (
  countryCode: string | null | undefined,
  region: string | null | undefined
): CrossBorderCountries | undefined => parseCrossBorderCountry(countryCode) ?? vendorRegionToCrossBorderCountry(region ?? undefined);

/** Whether the roaster's entered bruto already includes VAT and/or KS. */
export interface IncludedFlags {
  vatIncluded: boolean;
  ksIncluded: boolean;
}

/**
 * Recovers the net (pre-VAT, pre-KS) price from the bruto value the roaster
 * typed, given which components that value already includes:
 *   - neither            → net = entered
 *   - VAT only           → net = entered / (1 + vat)
 *   - VAT and KS         → net = entered / (1 + vat) − ks
 *   - KS only (undefined per the design) → treated as net = entered
 */
export const deriveNetto = (entered: number, { vatIncluded, ksIncluded }: IncludedFlags, vatRate: number, ks: number): number => {
  if (vatIncluded && ksIncluded) return removeTax(entered, vatRate) - ks;
  if (vatIncluded) return removeTax(entered, vatRate);
  return entered;
};

/** Net / KS / VAT / total breakdown for the home (editable) region. VAT is
 *  charged on (net + KS); the total is the bruto the roaster is declaring. */
export interface RegionPriceBreakdown {
  net: number;
  ks: number;
  vat: number;
  total: number;
}

/**
 * Net / KS / VAT / total for an explicit coffee-tax amount and VAT rate. VAT is
 * charged on (net + KS). Used for derived ship-to regions, where the RRP leg
 * takes the destination VAT and the wholesale leg the origin (home) VAT, but
 * both legs carry the destination's KS.
 *
 * `total` is rounded exactly once, from the unrounded `net`/`ks` — never from
 * the displayed `vat`. Summing an already-rounded VAT line back into the total
 * is a double-rounding bug: rounding VAT to its display cent can nudge the
 * running sum across a rounding boundary the true (unrounded) total wouldn't
 * cross, shifting the total by a whole cent (reported for DE→NL RRPs where the
 * true VAT lands just above a half-cent boundary). The displayed `vat` is then
 * back-solved as the residual (`total − net − ks`) rather than independently
 * rounded, so the three visible breakdown rows always sum to `total` exactly.
 */
export const computeBreakdown = (net: number, ks: number, vatRate: number): RegionPriceBreakdown => {
  const roundedNet = roundMoney(net);
  const total = roundMoney((net + ks) * (1 + vatRate));
  return { net: roundedNet, ks, vat: roundMoney(total - roundedNet - ks), total };
};

export const computeRegionBreakdown = (net: number, rates: RegionTaxRates, weightKg: number | null): RegionPriceBreakdown =>
  computeBreakdown(net, ksForRegion(weightKg, rates), rates.vatRate);

/** Inputs for deriving every ship-to region's prices from the home-region entry. */
export interface SkuPriceInput {
  /** Bruto MSRP the roaster typed for the home region. */
  msrp: number;
  /** Bruto wholesale price the roaster typed for the home region. */
  wholesalePrice: number;
  flags: IncludedFlags;
  homeRegion: CrossBorderCountries;
  /** All selected ship-to regions (must include the home region). */
  shipTo: CrossBorderCountries[];
  weightKg: number | null;
  ratesByRegion: TaxRatesByRegion;
}

/** Full RRP + wholesale tax breakdown for one ship-to region. */
export interface RegionPriceBreakdowns {
  region: CrossBorderCountries;
  /** VAT rate applied to the RRP leg — always the destination's own VAT. */
  msrpVatRate: number;
  /** VAT rate applied to the wholesale leg — the origin (home) VAT for derived
   *  regions, the region's own VAT for the home region. */
  wholesaleVatRate: number;
  msrp: RegionPriceBreakdown;
  wholesale: RegionPriceBreakdown;
}

/**
 * Builds the full per-region RRP + wholesale breakdowns from the home-region
 * entry.
 *
 * - **Home region:** both legs use the home VAT and carry the home coffee tax.
 * - **Derived RRP:** destination VAT + destination KS — the German end customer
 *   pays the coffee tax in the retail price.
 * - **Derived wholesale:** home VAT and **no destination KS** — the roaster's
 *   wholesale invoice is identical for every destination because BRG lodges the
 *   Kaffeesteuer on the roaster's behalf (2026-06-25 decision).
 */
export const computeRegionPriceBreakdowns = ({
  msrp,
  wholesalePrice,
  flags,
  homeRegion,
  shipTo,
  weightKg,
  ratesByRegion,
}: SkuPriceInput): RegionPriceBreakdowns[] => {
  // The roaster's input isn't constrained to 2 decimals (e.g. "5.423"). Rounding
  // to the nearest cent BEFORE deriving netto — rather than rounding the
  // intermediate net/VAT pieces of an un-rounded bruto — is what makes the whole
  // net → VAT → total round-trip reproduce the entered price exactly: every
  // genuine 2-decimal EUR amount round-trips losslessly through this pipeline,
  // whereas a 3-decimal input forces an ambiguous choice between rounding the
  // input directly vs. rounding its derived parts, which can drift by a cent.
  const roundedMsrp = roundMoney(msrp);
  const roundedWholesalePrice = roundMoney(wholesalePrice);
  const homeRates = ratesByRegion[homeRegion];
  const ksHome = ksForRegion(weightKg, homeRates);
  const netMsrp = deriveNetto(roundedMsrp, flags, homeRates.vatRate, ksHome);
  const netWholesale = deriveNetto(roundedWholesalePrice, flags, homeRates.vatRate, ksHome);

  return shipTo.map((region) => {
    const rates = ratesByRegion[region];
    const isHome = region === homeRegion;
    const ksDest = ksForRegion(weightKg, rates);
    // Wholesale carries KS only for the home region; for every other destination
    // BRG lodges the coffee tax, so the wholesale invoice stays unchanged.
    const wholesaleKs = isHome ? ksDest : 0;
    const wholesaleVatRate = isHome ? rates.vatRate : homeRates.vatRate;
    return {
      region,
      msrpVatRate: rates.vatRate,
      wholesaleVatRate,
      msrp: computeBreakdown(netMsrp, ksDest, rates.vatRate),
      wholesale: computeBreakdown(netWholesale, wholesaleKs, wholesaleVatRate),
    };
  });
};

/**
 * The wholesale price persisted/sent to the backend: netto + VAT only, with NO
 * coffee tax (Kaffeesteuer). BRG lodges the KS for every destination — including
 * the roaster's home region — so the wholesale invoice never carries KS. A
 * region without KS (e.g. NL) already equals its displayed wholesale total, so
 * the rule only changes KS regions (DE) in practice.
 */
export const wholesaleSendTotal = ({ wholesale, wholesaleVatRate }: RegionPriceBreakdowns): number =>
  roundMoney(wholesale.net * (1 + wholesaleVatRate));

/**
 * Reconstructs the coffee-tax-INCLUSIVE gross wholesale price for DISPLAY from a
 * stored KS-excluded value (netto + VAT): `((amount − VAT) + KS) + VAT`. The
 * read-only price displays (wholesale cell, ship-to flags) and the price-update
 * popover's editable default use this so the roaster keeps seeing the
 * KS-inclusive bruto while the backend stores the KS-excluded amount. A region
 * with no KS (e.g. NL) returns the value unchanged.
 */
export const reconstructGrossWholesale = (sendTotal: number, ks: number, vatRate: number): number =>
  roundMoney(addTax(removeTax(sendTotal, vatRate) + ks, vatRate));

/**
 * Display wholesale for a stored (KS-excluded) value, given the region whose KS
 * applies. Reconstructs the KS-inclusive gross via {@link reconstructGrossWholesale};
 * returns the value unchanged when the region is unknown or levies no KS.
 */
export const displayWholesaleForRegion = (
  sendTotal: number,
  region: CrossBorderCountries | undefined,
  ratesByRegion: TaxRatesByRegion,
  weightKg: number | null
): number => {
  if (!region) return sendTotal;
  const rates = ratesByRegion[region];
  return reconstructGrossWholesale(sendTotal, ksForRegion(weightKg, rates), rates.vatRate);
};

/**
 * Maps computed per-region breakdowns to the persisted `skuPrices`: RRP keeps
 * its displayed gross total; wholesale is the KS-excluded send value (netto +
 * VAT, see {@link wholesaleSendTotal}). When the roaster's home region is DE,
 * every entry additionally carries `roasterWholesalePrice` — the bruto
 * wholesale value shown in that region's UI field, with no tax derivation
 * applied on top — so the backend can reconcile the Kaffeesteuer against what
 * was actually shown/entered. That's the raw typed value for the home entry
 * (the only editable field) and the derived gross total for every other
 * region (what its read-only wholesale field displays). Absent entirely for
 * non-DE-home roasters (e.g. an NL roaster shipping to DE).
 */
export const breakdownsToSkuPrices = (
  breakdowns: RegionPriceBreakdowns[],
  rawWholesalePrice: number,
  homeRegion: CrossBorderCountries
): SkuRegionPrice[] => {
  // Rounded for the same reason as computeRegionPriceBreakdowns: this bypasses
  // that function's own rounding since it's passed straight from the caller's
  // (possibly >2-decimal) entered value.
  const roundedWholesalePrice = roundMoney(rawWholesalePrice);
  return breakdowns.map((entry) => ({
    region: entry.region,
    msrp: entry.msrp.total,
    wholesalePrice: wholesaleSendTotal(entry),
    ...(homeRegion === "DE" ? { roasterWholesalePrice: entry.region === homeRegion ? roundedWholesalePrice : entry.wholesale.total } : {}),
  }));
};

/**
 * Derives the per-region prices persisted as `skuPrices` from the home-region
 * entry. RRP keeps its gross bruto total; wholesale drops the coffee tax.
 */
export const computeSkuPrices = (input: SkuPriceInput): SkuRegionPrice[] =>
  breakdownsToSkuPrices(computeRegionPriceBreakdowns(input), input.wholesalePrice, input.homeRegion);

/**
 * Resolves the Kaffeesteuer-excluded wholesale price (netto + VAT) to send to
 * Salesforce for a DE-domestic variant, given the roaster-entered gross MSRP
 * and wholesale price. Shared by every submission path that needs to apply
 * the DE-domestic wholesale rule (SS onboarding, Shopify import).
 */
export const resolveDeDomesticWholesalePrice = (
  msrp: number,
  wholesalePrice: number,
  weightKg: number | null,
  ratesByRegion: TaxRatesByRegion
): number => {
  const [homeSkuPrice] = computeSkuPrices({
    msrp,
    wholesalePrice,
    flags: { vatIncluded: true, ksIncluded: true },
    homeRegion: "DE",
    shipTo: ["DE"],
    weightKg,
    ratesByRegion,
  });
  return homeSkuPrice?.wholesalePrice ?? wholesalePrice;
};

/** Parses a rate that may be expressed as a percent ("7") or fraction ("0.07").
 *  Values greater than 1 are treated as percentages. */
const parseVatRate = (value: string | undefined): number | null => {
  if (value === undefined) return null;
  const rate = Number.parseFloat(value);
  if (!Number.isFinite(rate)) return null;
  return rate > 1 ? rate / 100 : rate;
};

/**
 * Coffee-tax (KS) rate per kg from a config row, honouring an "…/lb" unit.
 * Returns 0 when KS is not applicable for the row. Keyed purely on
 * `regionalTaxApplicable` — the `regionalTaxName` label is informational and has
 * been seen mis-spelled ("coffeTax") in the data, so it is not relied on.
 */
const parseKsPerKg = (config: TaxConfiguration): number | null => {
  if (!config.regionalTaxApplicable) return 0;
  const rate = Number.parseFloat(config.regionalTaxRate ?? "");
  if (!Number.isFinite(rate)) return null;
  return config.regionalTaxRateUnit?.toLowerCase().includes("lb") ? rate / KG_PER_LB : rate;
};

const CROSS_BORDER_REGIONS: CrossBorderCountries[] = ["DE", "NL"];

/**
 * Maps tax-configuration API rows into the per-region rates the calculator
 * needs. The API returns either a wildcard row (`shippingCountryCode: "*"`,
 * applying to every destination) and/or country-specific rows ("DE"/"NL").
 * Wildcard rows are applied to all regions first, then country-specific rows
 * override them. Missing/unparseable values fall back to
 * `DEFAULT_TAX_RATES_BY_REGION`.
 */
export const taxConfigsToRegionRates = (
  configs: TaxConfiguration[] | undefined,
  fallback: TaxRatesByRegion = DEFAULT_TAX_RATES_BY_REGION
): TaxRatesByRegion => {
  const result: TaxRatesByRegion = { DE: { ...fallback.DE }, NL: { ...fallback.NL } };
  const applyConfig = (region: CrossBorderCountries, config: TaxConfiguration) => {
    result[region] = {
      vatRate: parseVatRate(config.saleTax) ?? fallback[region].vatRate,
      ksPerKg: parseKsPerKg(config) ?? fallback[region].ksPerKg,
    };
  };
  const rows = configs ?? [];
  rows
    .filter((config) => config.shippingCountryCode === "*")
    .forEach((config) => CROSS_BORDER_REGIONS.forEach((region) => applyConfig(region, config)));
  rows.forEach((config) => {
    if (config.shippingCountryCode === "DE" || config.shippingCountryCode === "NL") applyConfig(config.shippingCountryCode, config);
  });
  return result;
};

/** Returns the gross margin as a percentage: `(msrp - wholesale) / msrp * 100`.
 *  Returns 0 when msrp is zero or negative to avoid division by zero. Rounds
 *  both inputs to the nearest cent first — margin floors are validated against
 *  what's actually sent to the API, not the roaster's possibly >2-decimal raw
 *  input, so a value that rounds to an acceptable margin isn't rejected (or a
 *  value that rounds to a bad one isn't waved through) based on sub-cent noise. */
export const calculateMarginPercent = (msrp: number, wholesale: number): number => {
  const roundedMsrp = roundMoney(msrp);
  const roundedWholesale = roundMoney(wholesale);
  return roundedMsrp > 0 ? ((roundedMsrp - roundedWholesale) / roundedMsrp) * 100 : 0;
};

/** Effective ship-to list and whether the per-region Netto/KS/VAT/Final
 *  breakdown UI (and the KS-excluded wholesale-send rule) applies. */
export interface HomeRegionPricingContext {
  /** `rawShipTo` unchanged for a genuine cross-border list (more than one
   *  country); `[homeRegion]` for a DE-domestic vendor, whether `rawShipTo` was
   *  empty (domestic products don't populate cross-border ship-to) or already
   *  just `["DE"]` — so the tax engine always has a home entry. */
  shipTo: CrossBorderCountries[];
  /** Cross-border (more than one ship-to country) OR DE-domestic.
   *  NL-domestic is intentionally excluded — NL has no Kaffeesteuer and the
   *  ticket scopes this to DE only. */
  showRegionPricingUi: boolean;
}

/**
 * Resolves the ship-to list and breakdown-UI visibility shared by the SKU
 * import form, the price-update popover, and the products-table wholesale
 * cell. Centralizes the "DE-domestic has no ship-to data but still needs the
 * home-region breakdown" fallback so the three call sites don't each
 * reimplement it. Idempotent — safe to call again on an already-resolved
 * ship-to list.
 */
export const resolveHomeRegionPricingContext = (
  homeRegion: CrossBorderCountries | undefined,
  rawShipTo: CrossBorderCountries[]
): HomeRegionPricingContext => {
  if (rawShipTo.length > 1) return { shipTo: rawShipTo, showRegionPricingUi: true };
  const isDeDomestic = homeRegion === "DE" && (rawShipTo.length === 0 || rawShipTo[0] === "DE");
  if (isDeDomestic) return { shipTo: ["DE"], showRegionPricingUi: true };
  return { shipTo: rawShipTo, showRegionPricingUi: false };
};
